import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@material-ui/core'
import ArchiveIcon from '@material-ui/icons/Archive'
import MoreIcon from '@material-ui/icons/MoreVert'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

import { ConfirmActionModal } from '../../components/ConfirmActionModal'
import {
  PRICE_CLASSES_ARCHIVE_MUTATION,
  PRICE_CLASSES_DEARCHIVE_MUTATION
} from '../../graphql/priceClass'
import { gregorianToJalaaliWithHourMinuteAndSecond } from '../../helpers/date'
import { addCommaToPrice } from '../../helpers/numbers'
import { persianRegex } from '../../helpers/regex'
import { getTranslate, lang } from '../../localization'
import { actionTypes, PriceClass } from '../../types/priceClass'
import GeneratePriceClassModal from './GeneratePriceClassModal'
import useStyles from './styles/PriceClassRow.styles'

interface PriceClassRowProps {
  data: PriceClass
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      priceClasses: PriceClass[]
    }>
  >
  isSmallScreen: boolean
}

const PriceClassRow: React.FC<PriceClassRowProps> = ({
  data,
  refetch,
  isSmallScreen
}) => {
  const classes = useStyles()

  const [modifyModalIsOpen, setModifyModalIsOpenModalIsOpen] = useState(false)
  const [actionModalIsOpen, setActionModalIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null)
  const maxVisibleActions = 2

  const [archivePriceClass, { loading: loadingForArchive }] = useMutation(
    PRICE_CLASSES_ARCHIVE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp) => {
        setActionModalIsOpen(false)
        toast.success(resp.priceClassArchive.message)
        refetch()
      },
      onError: (e) => {
        toast.error(e.message)
      }
    }
  )

  const [deArchivePriceClass, { loading: loadingForDeArchive }] = useMutation(
    PRICE_CLASSES_DEARCHIVE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp) => {
        setActionModalIsOpen(false)
        toast.success(resp.priceClassDeArchive.message)
        refetch()
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const onClickConfirmButton = (priceClassID: string | undefined) => {
    if (data.archivedAt) {
      deArchivePriceClass({
        variables: {
          priceClassID: priceClassID
        }
      })
    } else {
      archivePriceClass({
        variables: {
          priceClassID: priceClassID
        }
      })
    }
  }

  const isPersianName = persianRegex.test(data.nameTranslatedList[0].value)

  return (
    <div>
      <div
        className={classes.root}
        onClick={() => {
          setModifyModalIsOpenModalIsOpen(true)
        }}>
        <div className={classes.nameCell}>
          <Typography
            variant="h4"
            style={isPersianName ? { fontFamily: 'IranSans' } : undefined}>
            {lang === 'en' ? data.name : data.nameTranslatedList[0].value}
          </Typography>
        </div>
        <div className={classes.nameCell}>
          {!isSmallScreen && (
            <Tooltip
              classes={{ tooltip: classes.toolTip }}
              placement="top-start"
              title={
                lang == 'fa' ? (
                  `${data.priceActions
                    .map((i, index) => {
                      const actionType = actionTypes.find(
                        (item) => item.value === i.actionType
                      )?.label

                      const beneficiaryText = i.beneficiary?.entityName
                        ? `${getTranslate(' for ')}${i.beneficiary.entityName}`
                        : ''

                      return (
                        (i.actionAmount.amount
                          ? addCommaToPrice(i.actionAmount.amount) +
                            (i.actionAmount.type === 'FixedFee'
                              ? ` ${getTranslate(
                                  i.actionAmount.toCurrency || ''
                                )} `
                              : '% ')
                          : '') +
                        getTranslate(actionType || '') +
                        beneficiaryText +
                        (index !== data.priceActions.length - 1 ? ' و ' : '')
                      )
                    })
                    .join('')}`
                ) : (
                  <span>
                    {data.priceActions.map((i, index) => {
                      const actionType = actionTypes.find(
                        (item) => item.value === i.actionType
                      )?.label

                      const amountText = i.actionAmount.amount
                        ? addCommaToPrice(i.actionAmount.amount) +
                          (i.actionAmount.type === 'FixedFee'
                            ? ` ${getTranslate(
                                i.actionAmount.toCurrency || ''
                              )} `
                            : '% ')
                        : ''

                      const separator =
                        index !== data.priceActions.length - 1 ? ' + ' : ''

                      return (
                        <React.Fragment key={index}>
                          <span dir="ltr">{amountText}</span>
                          {getTranslate(actionType || '')}
                          {i.beneficiary?.entityName && (
                            <>
                              {getTranslate(' for ')}
                              <span dir="rtl">{i.beneficiary?.entityName}</span>
                            </>
                          )}
                          {separator}
                        </React.Fragment>
                      )
                    })}
                  </span>
                )
              }
              aria-label="Price Actions Detail">
              {lang == 'fa' ? (
                <Typography variant="body1">
                  {data.priceActions
                    .slice(0, maxVisibleActions)
                    .map((i, index) => {
                      const actionType = actionTypes.find(
                        (item) => item.value === i.actionType
                      )?.label

                      const amountText = i.actionAmount.amount
                        ? addCommaToPrice(i.actionAmount.amount) +
                          (i.actionAmount.type === 'FixedFee'
                            ? ` ${getTranslate(
                                i.actionAmount.toCurrency || ''
                              )} `
                            : '% ')
                        : ''

                      const separator =
                        index < maxVisibleActions - 1 &&
                        index < data.priceActions.length - 1
                          ? ' و '
                          : ''

                      const isPersian = persianRegex.test(
                        i.beneficiary?.entityName || ''
                      )

                      return (
                        <React.Fragment key={index}>
                          {amountText}
                          {getTranslate(actionType || '')}
                          {i.beneficiary?.entityName
                            ? getTranslate(' for ')
                            : ''}
                          <strong
                            style={
                              isPersian ? { fontFamily: 'IranSans' } : undefined
                            }>
                            {i.beneficiary?.entityName}
                          </strong>
                          {separator}
                        </React.Fragment>
                      )
                    })}
                  {data.priceActions.length > maxVisibleActions ? ' و ...' : ''}
                </Typography>
              ) : (
                <Typography variant="body1">
                  {data.priceActions
                    .slice(0, maxVisibleActions)
                    .map((i, index) => {
                      const actionType = actionTypes.find(
                        (item) => item.value === i.actionType
                      )?.label

                      const amountText = i.actionAmount.amount
                        ? addCommaToPrice(i.actionAmount.amount) +
                          (i.actionAmount.type === 'FixedFee'
                            ? ` ${getTranslate(
                                i.actionAmount.toCurrency || ''
                              )} `
                            : '% ')
                        : ''

                      const separator =
                        index < maxVisibleActions - 1 &&
                        index < data.priceActions.length - 1
                          ? ' + '
                          : ''

                      const isPersian = persianRegex.test(
                        i.beneficiary?.entityName || ''
                      )

                      return (
                        <React.Fragment key={index}>
                          {amountText && <span dir="ltr">{amountText}</span>}
                          <span dir="ltr">
                            {getTranslate(actionType || '')}
                          </span>
                          {i.beneficiary?.entityName && (
                            <>
                              <span dir="ltr">{getTranslate(' for ')}</span>
                              <strong
                                style={
                                  isPersian
                                    ? { fontFamily: 'IranSans' }
                                    : undefined
                                }>
                                <span dir="rtl">
                                  {i.beneficiary?.entityName}
                                </span>
                              </strong>
                            </>
                          )}
                          {separator}
                        </React.Fragment>
                      )
                    })}
                  {data.priceActions.length > maxVisibleActions ? ' + ...' : ''}
                </Typography>
              )}
            </Tooltip>
          )}
        </div>
        <div className={classes.nameCell}>
          <Typography variant="h4">{data.code}</Typography>
        </div>
        {!isSmallScreen && (
          <>
            <div className={classes.nameCell}>
              <Typography variant="h4">{data.inputCurrency}</Typography>
            </div>
            <div className={classes.nameCell}>
              <Typography variant="h4">{data.outputCurrency}</Typography>
            </div>
            <div className={classes.nameCell}>
              <Typography variant="h4">
                {data.archivedAt
                  ? lang === 'en'
                    ? data.archivedAt
                    : gregorianToJalaaliWithHourMinuteAndSecond(data.archivedAt)
                  : ' '}
              </Typography>
            </div>
          </>
        )}
        <div>
          <IconButton onClick={handleClick}>
            <MoreIcon />
          </IconButton>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}>
            <MenuItem
              onClick={() => {
                setModifyModalIsOpenModalIsOpen(true)
              }}>
              {getTranslate('View')}
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation()
                setActionModalIsOpen(true)
              }}>
              {data.archivedAt
                ? getTranslate('DeArchive')
                : getTranslate('Archive')}
            </MenuItem>
          </Menu>
        </div>
      </div>
      {actionModalIsOpen && (
        <ConfirmActionModal
          title={
            data.archivedAt
              ? getTranslate('The priceClass is about to be dearchived')
              : getTranslate('The priceClass is about to be archived')
          }
          subtitle={
            data.archivedAt
              ? getTranslate('Are you sure you want to dearchive it?')
              : getTranslate('Are you sure you want to archive it?')
          }
          signature={data.archivedAt ? 'DeArchive Me' : 'Archive Me'}
          onClose={() => setActionModalIsOpen(false)}
          isOpen={actionModalIsOpen}
          onClick={() => onClickConfirmButton(data.id)}
          loading={loadingForArchive || loadingForDeArchive}
          icon={<ArchiveIcon className={classes.iconSize} />}
        />
      )}
      {modifyModalIsOpen && (
        <GeneratePriceClassModal
          isOpen={modifyModalIsOpen}
          onClose={() => setModifyModalIsOpenModalIsOpen(false)}
          refetch={refetch}
          data={data}
        />
      )}
    </div>
  )
}

export default PriceClassRow
